// Every character in the archive can be an avatar, grouped in tabs. The value
// saved is still the picture's URL (or the emoji), so old avatars keep working.
import React, { useMemo, useState } from 'react';
import characterProfiles from '../../data/characterProfiles';
import { AVATAR_OPTIONS, CHARACTER_AVATARS, DEFAULT_AVATAR } from '../../config/avatarConfig';
import './AvatarPicker.css';

const GROUPS = [
  { id: 'straw', label: 'Straw Hats', test: (c) => /straw hat/i.test(c) },
  { id: 'emperors', label: 'Emperors', test: (c) => /big mom|beasts|whitebeard|red hair|blackbeard|roger/i.test(c) },
  { id: 'navy', label: 'Marines', test: (c) => /marine|cp9|cp0|world government|sword|impel down|admiral/i.test(c) },
  { id: 'more', label: 'More pirates', test: () => true },
];

const buildTabs = () => {
  const seen = new Set();
  const tabs = GROUPS.map((g) => ({ ...g, items: [] }));
  const place = (label, image, crew) => {
    if (!image || seen.has(image)) return;
    seen.add(image);
    tabs.find((t) => t.test(crew || '')).items.push({ label, value: image });
  };
  // The original sixteen first, so the familiar faces stay at the front.
  CHARACTER_AVATARS.forEach((a) => {
    const p = characterProfiles.find((c) => c.image === a.image);
    place(a.label, a.image, p ? p.crew : a.id === 'whitebeard' ? 'Whitebeard Pirates' : a.id === 'shanks' ? 'Red Hair' : '');
  });
  characterProfiles.forEach((c) => place(c.name.split(' ').slice(-1)[0], c.image, c.crew));
  tabs.push({ id: 'icons', label: 'Icons', items: AVATAR_OPTIONS.map((o) => ({ label: o.label, value: o.emoji, emoji: true })) });
  return tabs.filter((t) => t.items.length);
};

export default function AvatarPicker({ value, onChange, disabled }) {
  const tabs = useMemo(buildTabs, []);
  // Open where their current pick is; a new player (default flag) starts on the Straw Hats.
  const [tab, setTab] = useState(() => (
    (value !== DEFAULT_AVATAR && tabs.find((t) => t.items.some((i) => i.value === value))) || tabs[0]
  ).id);
  const current = tabs.find((t) => t.id === tab) || tabs[0];

  return (
    <div className="ap">
      <div className="ap-tabs" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={t.id === tab}
            className={`ap-tab ${t.id === tab ? 'on' : ''}`} onClick={() => setTab(t.id)}>
            {t.label} <span className="ap-count">{t.items.length}</span>
          </button>
        ))}
      </div>
      <div className="ap-grid">
        {current.items.map((item) => (
          <button key={item.value} type="button" title={item.label} disabled={disabled}
            className={`ap-item ${value === item.value ? 'on' : ''}`} onClick={() => onChange(item.value)}>
            {item.emoji
              ? <span className="ap-emoji">{item.value}</span>
              : <img src={item.value} alt={item.label} loading="lazy" />}
          </button>
        ))}
      </div>
    </div>
  );
}
